import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from "@mui/material";
import CategoryIcon from "@mui/icons-material/Category";
import RestoreIcon from "@mui/icons-material/Restore";
import SecurityIcon from "@mui/icons-material/Security";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { Check, Copy } from "lucide-react";
import Agente from "./Agente";
import Contexto from "../Gestao/ContextoComponent";
import SecInfoHistorico from "../Gestao/SecInfoHistorico";

const ACConfigTab = ({ produto = "AC" }) => {
  const [contextos, setContextos] = useState([]);
  const [atualizaContexto, setAtualizaContexto] = useState(true);
  const [deveUpdateContexto, setDeveUpdateContexto] = useState(false);
  const [isLoadingContextos, setIsLoadingContextos] = useState(produto !== "posv");
  const [tabAtiva, setTabAtiva] = useState(0);
  const [mostrarExemplo, setMostrarExemplo] = useState(false);
  const [copied, setCopied] = useState(false);

  const textExemplo = `Exemplo de Informações Adicionais 1:

Sobre a Empresa
Nossa empresa foi fundada em 2020 e atua no mercado de tecnologia...

Contatos
Email: contato@empresa.com
Telefone: (11) 98765-4321`;

  const getNomeProduto = (produtoAtual) => {
    switch (produtoAtual) {
      case "AC":
        return {
          produtoRetorno: "Agente de Conteúdo",
          pessoaRespondida: "Usuários",
          nomesContextos: ["Informações adicionais 1", "Informações adicionais 2"],
        };
      case "sdr":
        return {
          produtoRetorno: "Agente de Pr\u00e9-Venda",
          produtoLabel: "Agente de Pr\u00e9 Venda",
          pessoaRespondida: "Leads",
          nomesContextos: ["Contexto institucional", "Contexto produto"],
        };
      case "posv":
        return {
          produtoRetorno: "Agente de Pós-Venda",
          produtoLabel: "Agente de Pós Venda",
          pessoaRespondida: "Clientes",
          nomesContextos: ["Informações adicionais"],
        };
      default:
        return {
          produtoRetorno: "Seu Agente",
          produtoLabel: "Seu Agente",
          pessoaRespondida: "Usuários",
          nomesContextos: ["Informações adicionais"],
        };
    }
  };

  const {
    produtoRetorno,
    produtoLabel = produtoRetorno,
    pessoaRespondida,
    nomesContextos,
  } = getNomeProduto(produto);
  const isContentAgent = produto === "AC";
  const isPosVenda = produto === "posv";
  const infoTabStartIndex = isPosVenda ? 1 : 0;
  const infoTabCount = isContentAgent ? 2 : 1;
  const historicoTabIndex = infoTabStartIndex + infoTabCount;
  const shouldShowContextTabs = !isPosVenda;
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const handleCopy = () => {
    navigator.clipboard.writeText(textExemplo);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    if (isPosVenda) {
      setIsLoadingContextos(false);
      return;
    }

    if (!atualizaContexto) return;

    const fetchContextos = async () => {
      setIsLoadingContextos(true);

      try {
        const token = localStorage.getItem("jwtToken");
        if (!token) return;

        const decodedToken = jwtDecode(token);
        const idClienteAipim = decodedToken.id_cliente;

        const response = await axios.get(`${baseURL}/produto/${produto}/contextos`, {
          params: { idClienteAipim },
          headers: { Authorization: `Bearer ${token}` },
        });

        const contextosProcessados = [];

        if (response.data.length > 0) {
          const dadosContexto = response.data[0];

          Object.keys(dadosContexto).forEach((chave) => {
            if (chave === "id" || chave === "idClienteAipim" || chave === "id_ClienteAipim") {
              return;
            }

            let nomeFormatado;

            if (produto === "AC") {
              nomeFormatado = chave.charAt(0).toUpperCase() + chave.slice(1).toLowerCase();
            } else if (produto === "sdr") {
              nomeFormatado = chave.charAt(0).toLowerCase() + chave.slice(1);
            } else {
              nomeFormatado = chave;
            }

            contextosProcessados.push({
              nome: nomeFormatado,
              conteudo: dadosContexto[chave],
            });
          });
        }

        setContextos(contextosProcessados);
      } catch (error) {
        console.error("Erro ao buscar dados contexto:", error);
      } finally {
        setAtualizaContexto(false);
        setIsLoadingContextos(false);
      }
    };

    fetchContextos();
  }, [atualizaContexto, baseURL, isPosVenda, produto]);

  useEffect(() => {
    if (deveUpdateContexto) {
      setAtualizaContexto(true);
      setDeveUpdateContexto(false);
    }
  }, [deveUpdateContexto]);

  if (isLoadingContextos) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
          gap: 2,
        }}
      >
        <CircularProgress size={40} thickness={4} color="primary" />
        <Typography sx={{ fontSize: "1rem", color: "text.secondary" }}>
          Carregando dados de configuração...
        </Typography>
      </Box>
    );
  }

  const getTabDescription = (tabAtual) => {
    if (isPosVenda && tabAtual === 0) return "Informações gerais do Agente";

    const infoTabIndex = tabAtual - infoTabStartIndex;

    if (isContentAgent) {
      if (infoTabIndex === 0) return "Informações adicionais 1";
      if (infoTabIndex === 1) return "Informações adicionais 2";
      return "";
    }

    if (infoTabIndex === 0) return "Informações adicionais";
    return "";
  };

  const contextoTabIndex = tabAtiva - infoTabStartIndex;
  const isAgenteTab = isPosVenda && tabAtiva === 0;
  const isHistoricoTab = tabAtiva === historicoTabIndex;
  const contextoSelecionado = contextos[contextoTabIndex] || contextos[0] || null;
  const nomeExibicaoAtual = nomesContextos[contextoTabIndex] || nomesContextos[0];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {!isPosVenda && (
        <Paper
          elevation={1}
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Tooltip
              title="Acesso restrito a administradores"
              arrow
              placement="top"
              slotProps={{ tooltip: { sx: { fontSize: 16, maxWidth: 250 } } }}
            >
              <SecurityIcon
                sx={{ mr: 1, fontSize: "18px", color: "primary.main", cursor: "help" }}
              />
            </Tooltip>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", lineHeight: 1.5, fontSize: "0.9rem" }}
            >
              Configure as informações do {produtoLabel}. Alterações afetam diretamente como
              seu Agente responde aos seus {pessoaRespondida}.
            </Typography>
          </Box>
        </Paper>
      )}

      <Paper
        elevation={1}
        sx={{
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Tabs
          value={tabAtiva}
          onChange={(_, novaTab) => setTabAtiva(novaTab)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.9rem" },
              fontWeight: 500,
              minWidth: { xs: 100, sm: 160 },
              py: 2,
              "& .MuiTab-iconWrapper": {
                marginBottom: "4px !important",
              },
            },
          }}
        >
          {isPosVenda && (
            <Tab icon={<SmartToyIcon sx={{ fontSize: "18px" }} />} label="Agente" />
          )}
          {shouldShowContextTabs && (
            <Tab icon={<CategoryIcon sx={{ fontSize: "18px" }} />} label={nomesContextos[0]} />
          )}
          {shouldShowContextTabs && isContentAgent && (
            <Tab
              icon={<CategoryIcon sx={{ fontSize: "18px" }} />}
              label={nomesContextos[1]}
            />
          )}
          {shouldShowContextTabs && (
            <Tab icon={<RestoreIcon sx={{ fontSize: "18px" }} />} label="Histórico de Alterações" />
          )}
        </Tabs>

        <Box
          sx={{
            px: 3,
            py: 1.5,
            backgroundColor: "background.default",
            borderBottom: "1px solid",
            borderColor: "divider",
            display: isHistoricoTab ? "none" : "block",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: "text.primary",
                fontSize: "0.85rem",
                fontStyle: "italic",
                fontWeight: "bold",
              }}
            >
              {getTabDescription(tabAtiva)}
            </Typography>
          </Box>

          <Dialog
            open={mostrarExemplo}
            onClose={() => setMostrarExemplo(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: { borderRadius: 2, bgcolor: "background.paper", color: "text.primary" },
            }}
          >
            <DialogTitle
              sx={{
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <HelpOutlineIcon />
              Exemplo de Informações Adicionais
            </DialogTitle>

            <DialogContent sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                sx={{ mb: 2, color: "text.secondary", fontWeight: 500 }}
              >
                Use este exemplo como referência para preencher as informações:
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: "background.default",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  position: "relative",
                  maxHeight: "400px",
                  overflow: "auto",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-line",
                    fontSize: "0.85rem",
                    color: "text.primary",
                    fontFamily: "monospace",
                    lineHeight: 1.6,
                    mb: 1,
                  }}
                >
                  {textExemplo}
                </Typography>
              </Paper>
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button
                onClick={handleCopy}
                variant="outlined"
                startIcon={copied ? <Check size={16} /> : <Copy size={16} />}
                sx={{
                  textTransform: "none",
                  color: copied ? "#10a37f" : "primary.main",
                  borderColor: copied ? "#10a37f" : "primary.main",
                }}
              >
                {copied ? "Copiado!" : "Copiar exemplo"}
              </Button>
              <Button
                onClick={() => setMostrarExemplo(false)}
                variant="contained"
                sx={{ textTransform: "none" }}
              >
                Fechar
              </Button>
            </DialogActions>
          </Dialog>
        </Box>

        <Box sx={{ p: 3 }}>
          {isAgenteTab && (
            <Agente
              endpointPath="/produto/posv/agente/infos"
              useClienteParams={false}
            />
          )}

          {!isAgenteTab && !isHistoricoTab && contextoSelecionado && (
            <Contexto
              key={`${contextoSelecionado.nome}-${tabAtiva}`}
              nomeContexto={contextoSelecionado.nome}
              conteudoContexto={contextoSelecionado.conteudo}
              setDeveUpdateContexto={setDeveUpdateContexto}
              produto={produto}
              nomeExibicao={nomeExibicaoAtual}
              rows={16}
              minEditorHeight={420}
            />
          )}

          {isHistoricoTab && (
            <SecInfoHistorico
              produtoRetorno={produtoRetorno}
              pessoaRespondida={pessoaRespondida}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ACConfigTab;
